import { useFlagAsInappropriate } from "@Components/account/mutations";
import { IProgressionDistanceChartValue } from "@Components/activity/types";
import { IActivityDetails } from "@Models/IActivityDetails";
import { Fixed, convertLength, getActivityMetric } from "@Utils";
import { Form, Input, Modal, notification } from "antd";
import React, { useState } from "react";

interface IUseActivityViewProps {
    activity: IActivityDetails;
    canRender: boolean;
    activities: IActivityDetails[];
}

const DEFAULT_TOGGLE_OPTIONS = {
    checkedChildren: "Imperial",
    unCheckedChildren: "Metric",
    className: "with-gradient",
};

const useActivityView = ({ activity, activities, canRender }: IUseActivityViewProps) => {
    // ** Hooks ** //
    const { mutateAsync: flagActivity } = useFlagAsInappropriate();
    const [form] = Form.useForm();

    // ** State Hooks ** //
    const [isDistanceGraphImperial, setIsDistanceGraphImperial] = useState(false);
    const [isProgressDistanceImperial, setIsProgressDistanceImperial] = useState(false);

    // ** Variables ** //
    const metricDistance = getActivityMetric("distance", activity);
    const metricDistanceGlobal = getActivityMetric("distance-global", activity);

    const distance = isDistanceGraphImperial ? convertLength(metricDistance, "metres", "yards") : metricDistance;
    const distanceGlobal = isDistanceGraphImperial
        ? convertLength(metricDistanceGlobal, "metres", "yards")
        : metricDistanceGlobal;

    if (!activity) return {};

    const progressionDistanceData: IProgressionDistanceChartValue[] = canRender
        ? activities
              ?.map((x) => {
                  const datumMetric = getActivityMetric("distance", x);
                  const datumDistance = isProgressDistanceImperial
                      ? convertLength(datumMetric, "metres", "yards")
                      : datumMetric;

                  return {
                      Date: x?.creationDate,
                      Distance: datumDistance,
                      ActivityInstanceId: x?.activityInstanceId,
                  };
              })
              .sort((a, b) => {
                  return new Date(a.Date).getTime() - new Date(b.Date).getTime();
              })
        : [];

    const progressionDistanceDataItem = {
        title: "Distance Travelled",
        description: "Distance travelled over successive attempts for this activity",
        options: {
            showLabels: false,
            currentAnnotation: {
                x: activity.creationDate,
                y: progressionDistanceData.find((item) => item.Date === activity.creationDate)?.Distance,
            },
            tooltipFormatter: (value, yField) => ({
                name: "Distance",
                value: `${Fixed(value[yField])}${isProgressDistanceImperial ? "yd" : "m"}`,
            }),
            values: progressionDistanceData,
            yAxisLabelFormatter: (value: number) => `${Fixed(value)}${isProgressDistanceImperial ? "yd" : "m"}`,
            yField: "Distance",
        },
        toggleOptions: {
            ...DEFAULT_TOGGLE_OPTIONS,
            onClick: () => setIsProgressDistanceImperial(!isProgressDistanceImperial),
        },
    };

    const distanceGraphOptions = {
        imperial: isDistanceGraphImperial,
        value: distance,
        target: distanceGlobal,
        toggleOptions: {
            ...DEFAULT_TOGGLE_OPTIONS,
            onClick: () => setIsDistanceGraphImperial(!isDistanceGraphImperial),
        },
    };

    const showProgressionDistanceChart =
        progressionDistanceData.length > 1 &&
        progressionDistanceData
            .map((x) => x.Distance)
            .reduce((x, y) => {
                return x + y;
            }, 0) > 0;

    // ** Handlers ** //

    const handleFlagActivity = () => {
        Modal.confirm({
            title: "Please give a reason why you're flagging this video?",
            cancelButtonProps: { className: "secondary danger" },
            content: (
                <Form form={form}>
                    <Form.Item name="notes" rules={[{ required: true, message: "You must give a reason" }]}>
                        <Input.TextArea autoSize={{ minRows: 3 }} maxLength={250} showCount />
                    </Form.Item>
                </Form>
            ),
            onOk: async (closeModal) => {
                await form.validateFields().then(({ notes }) => {
                    flagActivity(
                        { model: { notes, entityId: activity?.activityInstanceId, entityType: "activityInstance" } },
                        {
                            onSuccess: (res) => {
                                if (!res?.hasErrors && res?.value.id) {
                                    notification.success({ message: "The video has been flagged successfully." });
                                    closeModal();
                                }
                            },
                            onError(error) {
                                // eslint-disable-next-line no-console
                                console.error(error);
                            },
                        }
                    );
                });
            },
        });
    };

    return {
        distanceGraphOptions,
        handleFlagActivity,
        progressionDistanceDataItem,
        showProgressionDistanceChart,
    };
};

export default useActivityView;
